import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/auth';
import { toast } from 'react-toastify';

// Create the context
const AuthContext = createContext();

// Create provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const currentUser = authService.getCurrentUser();
        console.log("current user: " + currentUser);
        if (currentUser) {
          setUser(currentUser);
          
          // Refresh user data if needed
          try {
            const refreshedUser = await authService.refreshUserData();
            setUser(refreshedUser);
          } catch (error) {
            console.error('Failed to refresh user data', error);
            // Will automatically redirect to login due to 401 interceptor
          }
        }
      } catch (error) {
        console.error('Auth initialization error', error);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (credentials) => {
    setLoading(true);
    try {
      const data = await authService.login(credentials);
      setUser(data.user);
      return data;
    } catch (error) {
      toast.error(error.error || 'Login failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (userData) => {
    setLoading(true);
    try {
      const data = await authService.register(userData);
      setUser(data.user);
      return data;
    } catch (error) {
      toast.error(error.error || 'Registration failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    authService.logout();
    setUser(null);
  };

  // Value object that will be provided by the context
  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    initialized,
    login,
    register,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;